import React, { useEffect, useState, useCallback } from 'react';
import CorpusAwareChat from './components/CorpusAwareChat';
import Backroom from './components/Backroom';
import { bootExtensusEntity, EXTENSUS_ENTITY } from './extensusEntity';
import { startViewportAdapter } from './lib/adaptViewport';
import './index.css';

/** BOSSMAN cookie only — guests never learn operator role from this hook. */
function useOperatorSession() {
  const [session, setSession] = useState({
    checked: false,
    authenticated: false,
    role: null,
  });

  const refresh = useCallback(async () => {
    try {
      const res = await fetch('/api/extensus/session', {
        credentials: 'include',
        headers: { accept: 'application/json' },
      });
      const data = await res.json().catch(() => ({}));
      setSession({
        checked: true,
        authenticated: Boolean(data?.authenticated),
        role: data?.role || null,
      });
    } catch {
      setSession((prev) => ({ ...prev, checked: true, authenticated: false, role: null }));
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { session, setSession, refresh };
}

/** Live sovereign core health. BOSSMAN-only — do not poll ops memory for guests. */
function useSovereignHealth(pollMs = 45000, enabled = false) {
  const [health, setHealth] = useState(null);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const [stRes, memRes] = await Promise.all([
        fetch(`/api/extensus/status?_t=${Date.now()}`, { headers: { accept: 'application/json' } }),
        fetch(`/api/extensus/memory?view=latest&kind=HANDOFF&_t=${Date.now()}`, {
          headers: { accept: 'application/json' },
        }),
      ]);
      const st = stRes.ok ? await stRes.json().catch(() => null) : null;
      const mem = memRes.ok ? await memRes.json().catch(() => null) : null;
      setHealth({
        ok: Boolean(st?.ok || mem?.ok),
        observed_at: st?.observed_at || new Date().toISOString(),
        ollama: st?.ollama,
        bridge: st?.bridge,
        board: st?.board,
        d1: st?.d1,
        daemon: st?.daemon,
        grok: st?.grok,
        memory_mode: st?.memory?.mode,
        watermark: mem?.watermark?.generation || null,
        watermark_max_id: mem?.watermark?.sources?.[0]?.max_id ?? null,
        latest_handoff_id: mem?.hit?.id ?? null,
        soft_empty: Boolean(mem?.soft_empty),
        crawler: st?.crawler || null,
      });
    } catch (err) {
      setHealth({
        ok: false,
        error: String(err?.message || err),
        observed_at: new Date().toISOString(),
      });
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setHealth(null);
      return undefined;
    }
    refresh();
    const tick = () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      refresh();
    };
    const id = setInterval(tick, pollMs);
    const onVis = () => {
      if (typeof document !== 'undefined' && !document.hidden) refresh();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [refresh, pollMs, enabled]);

  return { health, refresh };
}

function pill(ok, label) {
  const cls = ok
    ? 'text-emerald-400 border-emerald-700/50 bg-emerald-950/40'
    : ok === false
      ? 'text-amber-400 border-amber-700/50 bg-amber-950/30'
      : 'text-zinc-500 border-zinc-800 bg-zinc-950';
  return (
    <span className={`px-1.5 py-0.5 rounded border text-[9px] tracking-wider ${cls}`}>{label}</span>
  );
}


/** Green SECURED / Red UNSECURED — Bossman password toggles public ops dump. */
function OpsSecuritySwitch() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [password, setPassword] = useState('');
  const [pendingSecured, setPendingSecured] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`/api/ops-security?_t=${Date.now()}`, { headers: { accept: 'application/json' } });
      const j = await r.json();
      if (j?.ok) setState(j);
    } catch (e) {
      setErr(String(e?.message || e));
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const onButtonClick = () => {
    setErr('');
    setPassword('');
    const currentlySecured = state == null ? true : Boolean(state.secured);
    // Click means flip
    setPendingSecured(!currentlySecured);
    setShowPw(true);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    setErr('');
    try {
      const r = await fetch('/api/ops-security', {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        body: JSON.stringify({ password, secured: pendingSecured }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) {
        setErr(j.error || j.hint || `http_${r.status}`);
        setBusy(false);
        return;
      }
      setState(j);
      setShowPw(false);
      setPassword('');
    } catch (ex) {
      setErr(String(ex?.message || ex));
    } finally {
      setBusy(false);
    }
  };

  const secured = state == null ? true : Boolean(state.secured);
  const label = secured ? 'SECURED' : 'UNSECURED';
  const cls = secured
    ? 'bg-emerald-600 hover:bg-emerald-500 text-black border-emerald-400 shadow-[0_0_12px_rgba(16,185,129,0.55)]'
    : 'bg-red-600 hover:bg-red-500 text-white border-red-400 shadow-[0_0_12px_rgba(239,68,68,0.55)] animate-pulse';

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button
        type="button"
        onClick={onButtonClick}
        title={
          secured
            ? 'SECURED — public full dump OFF. Click + Bossman password to temporarily unsecure.'
            : 'UNSECURED — public full dump ON. Click + Bossman password to secure/revoke.'
        }
        className={`px-3 py-1 text-[10px] font-black uppercase tracking-widest border rounded ${cls}`}
        data-testid="ops-security-switch"
      >
        {label}
      </button>
      {showPw && (
        <form onSubmit={submit} className="flex items-center gap-1 bg-zinc-950 border border-zinc-700 px-2 py-1 rounded">
          <input
            type="password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Bossman password"
            className="bg-black border border-zinc-700 text-xs px-2 py-1 w-36 text-white outline-none focus:border-zinc-400"
          />
          <button
            type="submit"
            disabled={busy || !password}
            className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 border border-zinc-600 hover:bg-white hover:text-black disabled:opacity-40"
          >
            {busy ? '…' : pendingSecured ? 'Lock secure' : 'Unlock open'}
          </button>
          <button
            type="button"
            onClick={() => {
              setShowPw(false);
              setErr('');
            }}
            className="text-[10px] text-zinc-500 px-1"
          >
            ✕
          </button>
        </form>
      )}
      {err && <span className="text-[10px] text-red-400 max-w-[14rem] truncate">{err}</span>}
    </div>
  );
}


function readStoredBrain() {
  try {
    const v = String(localStorage.getItem('extensus-operator-brain') || '').toLowerCase();
    if (v === 'grok' || v === 'extensus') return v;
  } catch { /* private mode */ }
  return 'extensus';
}

function BrainSwitch({ brain, onChange, grokConnected }) {
  const btn = (id, label) => (
    <button
      key={id}
      type="button"
      data-testid={`brain-switch-${id}`}
      aria-pressed={brain === id}
      onClick={() => onChange(id)}
      className={`px-3 py-1.5 text-[10px] font-black tracking-widest uppercase transition-all ${
        brain === id
          ? 'bg-white text-black shadow-[0_0_10px_rgba(255,255,255,0.7)]'
          : 'bg-zinc-950 text-zinc-400 hover:text-white'
      }`}
    >
      {label}
    </button>
  );
  return (
    <div
      className="flex border border-zinc-700 overflow-hidden"
      role="group"
      aria-label="Switch brain"
      data-testid="brain-switch"
      title={grokConnected === false ? 'Grok talks via xAI. EXTENSUS is local Ollama on this Mac.' : 'Grok or EXTENSUS'}
    >
      {btn('grok', 'Grok')}
      {btn('extensus', 'EXTENSUS')}
    </div>
  );
}

export default function App() {
  const [activeTab, setActiveTab] = useState('chat');
  const [authTrigger, setAuthTrigger] = useState(0);
  const [entityBoot, setEntityBoot] = useState(null);
  const [brain, setBrain] = useState(readStoredBrain);
  const { session, setSession } = useOperatorSession();
  const isBossman = session.authenticated && session.role === 'master';
  const { health, refresh: refreshHealth } = useSovereignHealth(45000, isBossman);

  useEffect(() => {
    if (!isBossman && activeTab !== 'chat') setActiveTab('chat');
  }, [isBossman, activeTab]);

  useEffect(() => startViewportAdapter(), []);

  useEffect(() => {
    if (!isBossman) {
      setEntityBoot(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const result = await bootExtensusEntity({ source: 'jarvis-3ue.pages.dev' });
      if (!cancelled) setEntityBoot(result);
    })().catch((err) => {
      if (!cancelled) {
        setEntityBoot({
          wired: false,
          roleplay: false,
          error: String(err?.message || err),
          entity: EXTENSUS_ENTITY.product,
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [isBossman]);

  const tabBtn = (id, label) => (
    <button
      key={id}
      onClick={() => setActiveTab(id)}
      className={`px-4 py-1.5 text-xs font-bold uppercase tracking-widest transition-all rounded-none ${
        activeTab === id
          ? 'bg-white text-black font-extrabold shadow-[0_0_10px_rgba(255,255,255,0.7)]'
          : 'bg-zinc-950 text-zinc-400 border border-zinc-800 hover:text-white hover:border-zinc-600'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="extensus-shell w-full bg-black text-white font-mono selection:bg-white selection:text-black min-h-screen flex flex-col">
      <nav className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-zinc-800 bg-black gap-3 flex-wrap">
        <div className="flex items-center space-x-3">
          <div
            className={`w-2.5 h-2.5 ${
              isBossman && entityBoot?.wired
                ? 'bg-emerald-400 shadow-[0_0_10px_#34d399]'
                : 'bg-white shadow-[0_0_10px_#ffffff]'
            } ${isBossman ? 'animate-pulse' : ''}`}
          />
          <div className="flex flex-col">
            <span className="font-black tracking-widest text-sm text-white uppercase drop-shadow-[0_0_8px_rgba(255,255,255,0.6)]">
              EXTENSUS
            </span>
            <span className="text-[9px] text-zinc-500 tracking-wider font-semibold uppercase">
              PERCEIVE. PLAN. DELEGATE. VERIFY. EXECUTE.
            </span>
          </div>
        </div>
        <div className="flex space-x-2 flex-wrap items-center">
          {!session.authenticated ? (
            <button
              type="button"
              onClick={() => {
                setActiveTab('chat');
                setAuthTrigger((n) => n + 1);
              }}
              className="px-4 py-1.5 text-xs font-mono font-bold tracking-widest uppercase rounded-none border border-red-600/90 bg-red-950/80 text-red-200 hover:bg-red-900/90 hover:text-white shadow-[0_0_12px_rgba(239,68,68,0.35)] flex items-center gap-2 transition-all"
            >
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              SIGN IN ▾
            </button>
          ) : (
            tabBtn('chat', 'CHAT')
          )}
          {isBossman ? (
            <BrainSwitch
              brain={brain}
              grokConnected={health?.grok?.connected}
              onChange={(next) => {
                setBrain(next);
                try { localStorage.setItem('extensus-operator-brain', next); } catch { /* private mode */ }
              }}
            />
          ) : null}
          {isBossman ? tabBtn('backroom', 'BACKROOM') : null}
          {isBossman ? <OpsSecuritySwitch /> : null}
        </div>
      </nav>

      {isBossman ? (
        <div
          className="px-4 sm:px-6 py-1.5 text-[10px] border-b border-zinc-900 bg-zinc-950/80 flex flex-wrap gap-2 items-center"
          data-testid="sovereign-core-strip"
        >
          <span
            className={entityBoot?.wired ? 'text-emerald-400 tracking-widest uppercase font-bold' : 'text-amber-400 tracking-widest uppercase font-bold'}
            data-testid="extensus-entity-strip"
          >
            {entityBoot?.wired ? 'Online' : entityBoot ? 'Core down' : 'Boot…'}
          </span>
          {health ? (
            <>
              {pill(
                Boolean(health.ollama?.ok),
                health.ollama?.ok
                  ? `OLLAMA${health.ollama?.quant ? ` ${health.ollama.quant}` : health.ollama?.model && /q5/i.test(health.ollama.model) ? ' Q5' : ''}`
                  : 'OLLAMA↓',
              )}
              {pill(Boolean(health.bridge?.ok), health.bridge?.ok ? 'BRIDGE' : 'BRIDGE↓')}
              {pill(Boolean(health.d1?.ok), health.d1?.ok ? `D1 r${health.d1.revision}` : 'D1↓')}
              {pill(Boolean(health.daemon?.ok), health.daemon?.ok ? 'DAEMON' : 'DAEMON↓')}
              {pill(
                health.grok?.connected === true,
                health.grok?.connected ? 'GROK' : 'GROK○',
              )}
              {pill(Boolean(health.board?.ok), health.board?.ok ? `LANES ${health.board?.present ?? 0}` : 'LANES↓')}
              {pill(
                health.watermark_max_id != null,
                health.watermark_max_id != null ? `WM#${health.watermark_max_id}` : (health.soft_empty ? 'WM empty' : 'WM…'),
              )}
              {health.ollama?.model ? (
                <span className="text-zinc-500 truncate max-w-[12rem]" title={health.ollama.model}>
                  {String(health.ollama.model).replace(/^llama3\.1:/, '')}
                </span>
              ) : null}
              {health.latest_handoff_id != null ? (
                <span className="text-zinc-500">#{health.latest_handoff_id}</span>
              ) : null}
              {health.crawler?.present ? pill(
                health.crawler.mapped === true,
                health.crawler.mapped
                  ? 'MAP ready'
                  : `MAP ${(Number(health.crawler.eligible) || 0) - (Number(health.crawler.remaining) || 0)}/${health.crawler.eligible ?? '?'}`,
              ) : null}
              {health.error ? <span className="text-red-400">{health.error}</span> : null}
            </>
          ) : (
            <span className="text-zinc-600">probing…</span>
          )}
          {entityBoot?.error ? <span className="text-red-400">{entityBoot.error}</span> : null}
          {entityBoot && !entityBoot.wired ? (
            <button
              type="button"
              className="underline text-amber-300"
              onClick={() => {
                setEntityBoot(null);
                bootExtensusEntity({ source: 'manual-retry' })
                  .then(setEntityBoot)
                  .catch((err) =>
                    setEntityBoot({
                      wired: false,
                      roleplay: false,
                      error: String(err?.message || err),
                      entity: EXTENSUS_ENTITY.product,
                    }),
                  );
              }}
            >
              Retry
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => refreshHealth()}
            className="ml-auto text-[9px] uppercase tracking-widest text-zinc-500 hover:text-white border border-zinc-800 px-2 py-0.5 rounded"
          >
            Refresh
          </button>
        </div>
      ) : null}

      <main className="extensus-main flex-1 p-2 sm:p-4">
        {activeTab === 'chat' && (
          <CorpusAwareChat
            authTrigger={authTrigger}
            entityBoot={isBossman ? entityBoot : null}
            health={isBossman ? health : null}
            operatorChrome={isBossman}
            brain={isBossman ? brain : 'extensus'}
            onSessionChange={(next) => {
              setSession({
                checked: true,
                authenticated: Boolean(next?.authenticated),
                role: next?.role || null,
              });
            }}
          />
        )}
        {isBossman && activeTab === 'backroom' && <Backroom channel="jarvis-gemini-transition" />}
      </main>
    </div>
  );
}
